import type { MutableRefObject } from 'react'

import PropTypes from 'prop-types'
import { useState, useEffect, useRef, Children, isValidElement, cloneElement } from 'react'
import styled from 'styled-components'

import { elementStyle } from 'styles/element-style'

interface MapProps extends google.maps.MapOptions {
  style?: Record<string, string>
  onClick?: (e: google.maps.MapMouseEvent) => void
  onZoom?: () => void
  onDragStart?: () => void
  onIdle?: (map: google.maps.Map) => void
  onLoad?: (map: google.maps.Map) => void
  onFullScreen?: () => void
  onExitFullScreen?: () => void
  hidden: boolean
}

const MapWrapper = styled.div`
  ${elementStyle}
  position: relative;
`
const eventNames = ['click', 'dragstart', 'idle']

export const Map: React.FC<MapProps> = ({
  onClick,
  onIdle,
  onZoom,
  onDragStart,
  onLoad,
  onFullScreen,
  onExitFullScreen,
  children,
  style,
  hidden,
  ...options
}) => {
  const ref = useRef<HTMLDivElement>() as MutableRefObject<HTMLInputElement>
  const [map, setMap] = useState<google.maps.Map>()

  const withFullScreenCheck = (toCallFunc: VoidFunction) => {
    const googleMapDiv = map?.getDiv().querySelector('.gm-style')
    if (
      window.innerHeight === googleMapDiv?.clientHeight &&
      window.innerWidth === googleMapDiv?.clientWidth
    ) {
      toCallFunc()
    } else {
      onExitFullScreen?.()
    }
  }

  useEffect(() => {
    if (ref.current && !map) {
      setMap(new google.maps.Map(ref.current, {}))
    }
  }, [ref, map])

  useEffect(() => {
    if (map) {
      eventNames.forEach((eventName) => google.maps.event.clearListeners(map, eventName))

      if (onClick) {
        map.addListener('click', onClick)
      }

      if (onZoom) {
        map.addListener('zoom_changed', onZoom)
      }

      if (onDragStart) {
        map.addListener('dragstart', onDragStart)
      }

      if (onIdle) {
        /*
            How to avoid onIdle is called continuously stuck in a loop
            - https://github.com/googlemaps/js-samples/issues/946
        */
        map.addListener('idle', () => onIdle(map))
      }

      if (onFullScreen) {
        map.addListener('idle', () => withFullScreenCheck(onFullScreen))
      }
    }
  }, [map, onClick, onDragStart, onIdle, onZoom, onFullScreen])

  useEffect(() => {
    if (map) {
      map.setOptions(options)
    }
  }, [map])

  useEffect(() => {
    if (map && onLoad) {
      onLoad(map)
    }
  }, [map, onLoad])

  return (
    <MapWrapper ref={ref} id="map" style={style} hidden={hidden}>
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement(child, { map })
        }
        return null
      })}
    </MapWrapper>
  )
}

Map.propTypes = {
  onClick: PropTypes.func,
  onIdle: PropTypes.func,
  onZoom: PropTypes.func,
  onDragStart: PropTypes.func,
  onLoad: PropTypes.func,
  style: PropTypes.objectOf(PropTypes.string.isRequired).isRequired,
  hidden: PropTypes.bool.isRequired,
}

Map.defaultProps = {
  onClick: undefined,
  onZoom: undefined,
  onDragStart: undefined,
  onLoad: undefined,
  onIdle: undefined,
}
